import React, { useEffect, useState } from "react";
import API from "../../../Utils/api";
import toast from "react-hot-toast";
import GalleryCardSkeleton from "./GalleryCardSkeleton";
import GalleryCard from "./GalleryCard";
import GalleryViewModal from "./GalleryViewModal";
import EditGalleryModal from "./EditGalleryModal";

const ITEMS_PER_PAGE = 8;

const AllGallery = ({ refresh, onRefresh }) => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewGallery, setViewGallery] = useState(null);
  const [editGallery, setEditGallery] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchGalley = async () => {
    setLoading(true);
    try {
      const res = await API.get(
        `/gallery/get-all?page=${currentPage}&limit=${ITEMS_PER_PAGE}&search=${search}`,
      );

      setGallery(res.data?.gallery || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Fetch data when page OR search changes
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchGalley();
    }, 500);

    return () => clearTimeout(delay);
  }, [search, currentPage]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/gallery/delete/${deleteId}`);
      setDeleteId(null);
      onRefresh();
      toast.success("Your gallery has been Deleted successfully! 🎉");
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <>
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search gallery by title..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-4 py-2.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-sm transition"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Results Count */}
      {!loading && (
        <p className="text-sm text-gray-500 mb-4">
          Showing <span className="font-semibold text-gray-700">{total}</span>{" "}
          gallery
        </p>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <GalleryCardSkeleton key={i} />
            ))
          : gallery.map((gallery) => (
              <GalleryCard
                key={gallery._id}
                gallery={gallery}
                onView={() => setViewGallery(gallery)}
                onEdit={() => setEditGallery(gallery)}
                onDelete={() => setDeleteId(gallery._id)}
              />
            ))}
      </div>

      {/* Empty State */}
      {!loading && total === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <span className="text-6xl mb-4">📝</span>
          <p className="text-lg font-medium">No gallery found</p>
          <p className="text-sm">
            Try adjusting your search or add a new gallery
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-10">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-9 h-9 rounded-lg font-medium transition ${
                currentPage === i + 1
                  ? "bg-green-600 text-white"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Next →
          </button>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="text-center">
              <span className="text-5xl">🗑️</span>
              <h3 className="text-lg font-bold text-gray-800 mt-3">
                Delete Gallery?
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewGallery && (
        <GalleryViewModal
          gallery={viewGallery}
          onClose={() => setViewGallery(null)}
        />
      )}

      {/* Edit Modal */}
      {editGallery && (
        <EditGalleryModal
          gallery={editGallery}
          onClose={() => setEditGallery(null)}
          onSuccess={() => {
            setEditGallery(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default AllGallery;
